import { getWebBaseURL } from '../getWebBaseURL';
import { isKnownRoute, WEB_ROUTE_PATHS } from '../../webRoutePaths';
import { sanitizeRedirectPath } from '../sanitizeRedirectPath';

/**
 * 딥링크를 웹 링크로 변환합니다.
 * - WEB_ROUTE_PATHS에 정의된 경로: path 반환 (예: '/goods/123')
 * - 그 외 경로: baseURL + path 풀 URL 반환 (예: 'https://m.a-bly.com/events/1234')
 * - 외부 URL: 풀 URL 그대로 반환
 * - 미지원 딥링크: null 반환
 */
export const convertDeeplinkToWebLink = async (
  deeplink: string,
): Promise<string | null> => {
  const resolved = resolveDeeplinkPath(deeplink);
  if (resolved == null) return null;
  if (resolved.startsWith('http')) return resolved;
  if (isKnownRoute(resolved.split('?')[0])) return resolved;

  const baseURL = await getWebBaseURL();
  return `${baseURL}${resolved}`;
};

/**
 * 딥링크에서 웹 경로(path) 또는 풀 URL을 추출합니다.
 *
 * 특수 케이스:
 * - ably://webview → url 파라미터 추출 (네이티브 전용 경로 차단)
 * - ably://home → /
 * - ably://react/App → WEB_ROUTE_PATHS 기반 스크린 매핑
 *
 * 그 외: ably://scheme/path → /scheme/path (자동 매핑)
 */
const resolveDeeplinkPath = (deeplink: string): string | null => {
  if (deeplink.startsWith('ably://webview')) {
    return resolveWebviewPath(deeplink);
  }

  if (deeplink.startsWith('ably://home')) {
    return '/';
  }

  if (deeplink.startsWith('ably://search')) {
    return resolveSearchPath(deeplink);
  }

  if (deeplink.startsWith('ably://main/')) {
    return resolveDeeplinkPath(`ably://${deeplink.slice('ably://main/'.length)}`);
  }

  if (deeplink === 'ably://sign/' || deeplink === 'ably://sign') {
    return resolveSignPath('/login');
  }

  if (deeplink === 'ably://sign/in') {
    return resolveSignPath('/login/email');
  }

  if (deeplink.startsWith('ably://react/App')) {
    return getScreenRoutePath(deeplink);
  }

  // ably://goods/123?ref=home → /goods/123?ref=home
  const pathAndQuery = deeplink.slice('ably://'.length);
  const path = `/${pathAndQuery}`;
  const pathOnly = path.split('?')[0];
  if (isKnownRoute(pathOnly)) return path;
  return isWebAvailableDeeplinkPath(pathOnly) ? path : null;
};

const resolveWebviewPath = (deeplink: string): string | null => {
  const url = extractWebviewUrl(deeplink);
  if (url == null) return null;

  if (url.startsWith('http')) {
    const pathname = new URL(url).pathname.replace(/\/+/g, '/');
    return isNativeOnlyWebviewPath(pathname) ? null : url;
  }

  const path = `/${url.replace(/^\//, '')}`;
  return isNativeOnlyWebviewPath(path) ? null : path;
};

const getScreenRoutePath = (deeplink: string): string | null => {
  const searchParams = new URLSearchParams(deeplink.split('?')[1]);
  const screen = searchParams.get('screen');
  const extrasRaw = searchParams.get('extras');

  if (!screen) return null;

  const routePattern =
    WEB_ROUTE_PATHS[screen as keyof typeof WEB_ROUTE_PATHS];
  if (!routePattern) return null;

  if (!extrasRaw) {
    return routePattern.replace(/:[\w]+/g, '').replace(/\/+$/, '') || '/';
  }

  try {
    const extras = JSON.parse(extrasRaw) as Record<string, unknown>;
    const resolved = routePattern.replace(/:(\w+)/g, (_, param) => {
      const value = extras[param] ?? extras['sno'];
      return value != null ? String(value) : '';
    });

    return `${resolved}?extras=${encodeURIComponent(extrasRaw)}`;
  } catch {
    return null;
  }
};

const resolveSearchPath = (deeplink: string): string => {
  const params = new URLSearchParams(deeplink.split('?')[1]);
  const query = params.get('query');

  // query 없음 → 검색 보조(SearchHelper). show_keyboard는 호출자가 명시한 경우에만 보존
  if (query == null) {
    const qs = params.toString();
    return qs
      ? `${WEB_ROUTE_PATHS.SearchHelper}?${qs}`
      : WEB_ROUTE_PATHS.SearchHelper;
  }

  // query 있음 → 검색 결과(SearchResult)로 직행. 키 변환 없이 그대로 전달
  return `${WEB_ROUTE_PATHS.SearchResult}?${params.toString()}`;
};

/**
 * 로그인 경로에 복귀 경로(?redirect=)를 싣습니다.
 * - 절대 URL(href)이 아니라 상대 경로: 소비측이 내부 경로만 허용합니다.
 * - location 이 없으면(네이티브) 복귀 경로 없이 로그인 경로만 반환합니다.
 */
const resolveSignPath = (loginPath: string): string => {
  const location = typeof window === 'undefined' ? undefined : window.location;
  if (location == null) return loginPath;

  const redirect = sanitizeRedirectPath(
    `${location.pathname}${location.search}`,
  );
  return `${loginPath}?redirect=${encodeURIComponent(redirect)}`;
};

/** 웹뷰에서 열리지만 웹에서는 지원하지 않는 경로 패턴 (ably://webview?url=...) */
const NATIVE_ONLY_WEBVIEW_PATTERNS: RegExp[] = [
  /^\/ai-studio/,
  /^\/content\/fitting-rooms/,
  /^\/content\/ai-profile/,
  /^\/goods\/\d+\/reviews\/photo/,
];

/** 웹에서 지원하는 딥링크 경로 (ably://path → /path 자동 매핑 허용) */
const WEB_AVAILABLE_DEEPLINK_PATHS = [
  '/mypage',
  '/fun',
  '/events',
  '/cart',
  '/notifications',
  '/setting',
  '/style',
  '/overview',
  '/shoplive',
];

const isNativeOnlyWebviewPath = (path: string): boolean =>
  NATIVE_ONLY_WEBVIEW_PATTERNS.some(pattern => pattern.test(path));

const isWebAvailableDeeplinkPath = (path: string): boolean =>
  WEB_AVAILABLE_DEEPLINK_PATHS.some(prefix => path.startsWith(prefix));

const extractWebviewUrl = (deeplink: string): string | null => {
  const queryPart = deeplink.split('?').slice(1).join('?');
  const params = new URLSearchParams(queryPart);
  return params.get('url');
};
